import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Banknote, CreditCard, Lock, MessageCircle, Phone } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { api, eur } from "@/lib/api";
import { usePublicConfig, waLink, telLink, formatPhone } from "@/lib/config";
import Reveal from "@/components/Reveal";

export default function Checkout() {
  const { t, lang } = useLanguage();
  const { items, subtotal, shipping, clear, promo, setPromo } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: "", zip: "", country: "" });
  const [processing, setProcessing] = useState(false);
  const [stripeMode, setStripeMode] = useState("test");
  const [quote, setQuote] = useState(null);
  const [method, setMethod] = useState("card");
  const isGreece = (v) => ["gr", "greece", "grecia", "ελλάδα", "ελλαδα", "ellada", "hellas", "ελλάς"].includes((v || "").trim().toLowerCase());
  const cfg = usePublicConfig();
  const codFee = method === "cod" ? (cfg?.codFee || 0) : 0;

  useEffect(() => {
    if (!promo) {
      setQuote(null);
      return;
    }
    api.promoQuote(promo).then(setQuote).catch(() => setQuote({ invalid: true }));
  }, [promo]);

  const discount = quote && !quote.invalid
    ? quote.percent_off
      ? (subtotal * quote.percent_off) / 100
      : Math.min(quote.amount_off || 0, subtotal)
    : 0;

  useEffect(() => {
    api.config().then((c) => setStripeMode(c.stripe_mode || "test")).catch(() => {});
  }, []);

  const placeOrder = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      if (method === "cod") {
        const res = await api.paymentsCod({
          items: items.map((i) => ({ slug: i.slug, qty: i.qty, variant: i.variant })),
          customer: form,
          lang,
        });
        navigate(`/payment/success?cod=${res.order_id}`);
        return;
      }
      const res = await api.paymentsCheckout({
        items: items.map((i) => ({ slug: i.slug, qty: i.qty, variant: i.variant })),
        customer: form,
        origin_url: window.location.origin,
        lang,
        promo_code: promo || undefined,
        method,
      });
      window.location.href = res.checkout_url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 400 && detail === "coupon_reserved") {
        toast.error(t.acc3.restrictedCoupon);
      } else if (err.response?.status === 400 && promo) {
        toast.error(t.cart.promoInvalid);
        setPromo("");
      } else {
        toast.error(t.common.error);
      }
      setProcessing(false);
    }
  };

  if (items.length === 0) {
    return (
      <div data-testid="checkout-empty" className="flex min-h-screen flex-col items-center justify-center gap-6 pt-32">
        <p className="text-sm text-navy/50">{t.cart.empty}</p>
        <Link to="/shop" data-testid="checkout-empty-cta" className="btn-primary-luxe">{t.cart.emptyCta}</Link>
      </div>
    );
  }

  const field = (key, label, extra = {}) => (
    <div>
      <label htmlFor={`co-${key}`} className="label-luxe mb-2 block text-navy/50">{label}</label>
      <input
        id={`co-${key}`}
        data-testid={`checkout-${key}-input`}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="input-luxe"
        {...extra}
      />
    </div>
  );

  return (
    <div data-testid="checkout-page" className="bg-surface pt-24">
      <div className="mx-auto max-w-6xl px-6 pb-24">
        <Reveal>
          <h1 className="font-display text-5xl font-light tracking-tight text-navy">{t.co.title}</h1>
        </Reveal>

        <form onSubmit={placeOrder} className="mt-12 grid gap-12 lg:grid-cols-5">
          <div className="space-y-10 lg:col-span-3">
            <Reveal className="border border-navy/10 bg-white p-8">
              <h2 className="label-luxe mb-6 text-navy">{t.co.contact}</h2>
              <div className="grid gap-5 sm:grid-cols-2">
                {field("name", t.co.name, { required: true })}
                {field("email", t.co.email, { required: true, type: "email" })}
                <div className="sm:col-span-2">{field("phone", t.co.phone, { type: "tel", required: true, minLength: 5 })}</div>
              </div>
            </Reveal>

            <Reveal delay={1} className="border border-navy/10 bg-white p-8">
              <h2 className="label-luxe mb-6 text-navy">{t.co.ship}</h2>
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="sm:col-span-2">{field("address", t.co.address, { required: true })}</div>
                {field("city", t.co.city, { required: true })}
                {field("zip", t.co.zip, { required: true })}
                <div className="sm:col-span-2">
                  {field("country", t.co.country, {
                    required: true,
                    onChange: (e) => {
                      const v = e.target.value;
                      setForm((f) => ({ ...f, country: v, phone: isGreece(v) && !f.phone.trim() ? "+30 " : f.phone }));
                    },
                  })}
                </div>
              </div>
            </Reveal>

            <Reveal delay={2} className="border border-navy/10 bg-white p-8">
              <h2 className="label-luxe mb-4 flex items-center gap-2 text-navy">
                <Lock size={13} className="text-gold-dark" />
                {t.co.payMethod || t.co.payment}
              </h2>
              <div className="space-y-3" role="radiogroup" data-testid="payment-methods">
                {[
                  { id: "card", label: t.co.payCard || "Credit / Debit Card", icon: CreditCard, note: "Visa · Mastercard · Amex" },
                  { id: "revolut", label: t.co.payRevolut || "Revolut Pay", icon: Phone, note: "Revolut" },
                  { id: "cod", label: t.co.payCod || "Cash on Delivery", icon: Banknote, note: codFee > 0 ? `+${eur(codFee)}` : "" },
                ].map((m) => (
                  <label
                    key={m.id}
                    data-testid={`pay-method-${m.id}`}
                    className={`flex cursor-pointer items-center gap-3 border px-4 py-3 transition-colors ${
                      method === m.id ? "border-gold bg-gold/5" : "border-navy/10 bg-white hover:border-navy/25"
                    }`}
                  >
                    <input
                      type="radio"
                      name="payment-method"
                      value={m.id}
                      checked={method === m.id}
                      onChange={() => setMethod(m.id)}
                      className="accent-gold-dark"
                    />
                    <m.icon size={16} className="text-navy/60" />
                    <span className="flex-1 text-xs font-semibold text-navy">{m.label}</span>
                    {m.note && <span className="text-[10px] text-navy/40">{m.note}</span>}
                  </label>
                ))}
              </div>
              <p data-testid="checkout-demo-note" className="mt-4 text-xs font-light text-navy/45">
                {method === "cod" ? t.co.payCod : (stripeMode === "test" ? t.co.demo : t.demoLive)}
              </p>
              {method === "cod" && codFee > 0 && (
                <p data-testid="checkout-codfee-note" className="mt-2 border border-gold/50 bg-gold/10 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-gold-dark">
                  {t.co.codFee}: +{eur(codFee)}
                </p>
              )}
              {items.some((i) => i.cs) && method === "cod" && (
                <p data-testid="checkout-preorder-note" className="mt-2 border border-red-300 bg-red-50 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-red-600">{t.preorderNote}</p>
              )}
              {method !== "cod" && (
                <p data-testid="checkout-discount-hint" className="mt-2 text-xs font-light text-gold-dark">{t.co.discountHint}</p>
              )}
            </Reveal>
          </div>

          <Reveal delay={1} className="lg:col-span-2">
            <div className="sticky top-40 border border-navy/10 bg-white p-8">
              <h2 className="label-luxe mb-6 text-navy">{t.co.summary}</h2>
              <div className="space-y-4">
                {items.map((i) => (
                  <div key={i.key} data-testid={`checkout-item-${i.key}`} className="flex items-center gap-4">
                    <div className="h-14 w-12 shrink-0 overflow-hidden border border-navy/10 bg-surface">
                      <img src={i.image} alt={i.name} className="h-full w-full object-cover" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-navy">{i.name}</p>
                      <p className="text-[11px] text-navy/45">× {i.qty} · {i.variant} {t.fmt[i.unit] || t.fmt.caps}</p>
                    </div>
                    <p className="text-xs font-semibold text-navy">{eur(i.price * i.qty)}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 space-y-2 border-t border-navy/10 pt-6 text-sm text-navy/60">
                <div className="flex justify-between">
                  <span>{t.cart.subtotal}</span>
                  <span data-testid="checkout-subtotal">{eur(subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t.cart.shipping}</span>
                  <span data-testid="checkout-shipping">{shipping === 0 ? t.cart.free : eur(shipping)}</span>
                </div>
                {promo && quote && !quote.invalid && (
                  <div className="flex justify-between text-gold-dark" data-testid="checkout-promo-line">
                    <span>{t.cart.promoApplied}: <span className="font-mono">{promo}</span></span>
                    <span>−{eur(discount)}</span>
                  </div>
                )}
                {method === "cod" && codFee > 0 && (
                  <div className="flex justify-between" data-testid="checkout-codfee-line">
                    <span>{t.co.codFee}</span>
                    <span>+{eur(codFee)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-navy/10 pt-3 text-base font-semibold text-navy">
                  <span>{t.cart.total}</span>
                  <span data-testid="checkout-total">{eur(Math.max(0, subtotal - discount) + shipping + codFee)}</span>
                </div>
              </div>
              <button
                type="submit"
                data-testid="checkout-place-order-button"
                disabled={processing || (method === "cod" && items.some((i) => i.cs))}
                className="btn-primary-luxe mt-8 w-full disabled:opacity-50"
              >
                {processing ? t.co.processing : method === "cod" ? (t.co.placeCod || t.co.place) : t.co.place}
              </button>
              {(cfg?.whatsapp || cfg?.phone) && (
                <div data-testid="checkout-support" className="mt-4 border border-navy/10 bg-surface px-4 py-3">
                  <p className="text-[11px] font-light text-navy/60">{t.co.supportMsg}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-4">
                    {cfg?.whatsapp && (
                      <a href={waLink(cfg.whatsapp, t.co.supportMsg)} target="_blank" rel="noopener noreferrer" data-testid="checkout-support-whatsapp" className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-navy transition-colors hover:text-gold-dark">
                        <MessageCircle size={13} /> WhatsApp
                      </a>
                    )}
                    {cfg?.phone && (
                      <a href={telLink(cfg.phone)} data-testid="checkout-support-phone" className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-navy transition-colors hover:text-gold-dark">
                        <Phone size={13} /> {formatPhone(cfg.phone)}
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Reveal>
        </form>
      </div>
    </div>
  );
}
